import React from 'react';
import { Link } from 'react-router-dom';
import AppHeader from './AppHeader';
import Footer from './Footer';

interface PuzzleGame {
  id: string;
  title: string;
  description: string;
  icon: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  route: string;
  features: string[];
}

const PuzzleGamesSelection: React.FC = () => {
  const puzzleGames: PuzzleGame[] = [
    {
      id: 'sum-grid',
      title: 'Sum Grid',
      description: 'Fill the 3×3 grid using numbers 1-9 to match row and column sum targets. Perfect for practicing addition skills.',
      icon: '➕',
      difficulty: 'Easy',
      route: '/sum-grid',
      features: ['Addition practice', 'Logic puzzles', 'Daily challenges']
    },
    {
      id: 'multiplication-grid',
      title: 'Multiplication Grid',
      description: 'Master times tables by filling grids to match multiplication targets. Great for strengthening multiplication skills.',
      icon: '✖️',
      difficulty: 'Medium',
      route: '/multiplication-grid',
      features: ['Times tables', 'Mental math', 'Strategic thinking']
    },
    {
      id: 'combined-grid',
      title: 'Combined Grid',
      description: 'Ultimate challenging puzzle! Satisfy both sum AND multiplication targets simultaneously.',
      icon: '🔢',
      difficulty: 'Hard',
      route: '/combined-grid',
      features: ['Mixed operations', 'Complex logic', 'Expert challenge']
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700';
      case 'Medium': return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700';
      case 'Hard': return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-600';
    }
  };

  const getGameColor = (id: string) => {
    switch (id) {
      case 'sum-grid': return 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700';
      case 'multiplication-grid': return 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700';
      case 'combined-grid': return 'from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700';
      default: return 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700">
      <AppHeader />

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="text-8xl mb-6 animate-bounce">🧩</div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 dark:text-gray-100 mb-6">
            Choose Your Puzzle Challenge
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Test your mathematical skills with our collection of logic grid puzzles. 
            Each game offers a unique challenge to strengthen different aspects of your math abilities.
          </p>
          <div className="mt-8 inline-flex items-center space-x-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-6 py-3 rounded-full text-lg font-medium">
            <span>🎯</span>
            <span>New Daily Puzzles Every Day</span>
          </div>
        </div>

        {/* Games Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {puzzleGames.map((game) => (
            <div key={game.id} className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700">
              {/* Game Header */}
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">{game.icon}</div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">{game.title}</h3>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getDifficultyColor(game.difficulty)}`}>
                  {game.difficulty} Level
                </span>
              </div>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed text-center">
                {game.description}
              </p>

              {/* Features */}
              <div className="mb-8">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 text-center">Key Features:</h4>
                <div className="space-y-2">
                  {game.features.map((feature, index) => (
                    <div key={index} className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                      <span className="text-blue-500 dark:text-blue-400">✦</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Play Options */}
              <div className="space-y-3">
                {/* Daily Challenge Button */}
                <Link
                  to={game.route}
                  className={`block w-full bg-gradient-to-r ${getGameColor(game.id)} text-white px-6 py-4 rounded-xl font-bold text-lg text-center transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>📅</span>
                    <span>Daily Challenge</span>
                  </div>
                  <div className="text-sm font-normal opacity-90 mt-1">
                    One puzzle per day • Track your progress
                  </div>
                </Link>

                {/* Play for Fun Button */}
                <Link
                  to={`${game.route}?mode=practice`}
                  className="block w-full bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-6 py-4 rounded-xl font-bold text-lg text-center transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>🎮</span>
                    <span>Play for Fun</span>
                  </div>
                  <div className="text-sm font-normal opacity-75 mt-1">
                    Unlimited rounds • Same rules
                  </div>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info Section */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-8 border border-indigo-200 dark:border-indigo-700">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6 text-center">
            🧠 Why Play Logic Grid Puzzles?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Improve Focus</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Enhance concentration and problem-solving skills
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🧮</span>
              </div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Math Practice</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Strengthen arithmetic and mental math abilities
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🏆</span>
              </div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Track Progress</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Monitor improvements with personal progress tracking
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎮</span>
              </div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Daily Fun</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                New puzzles every day keep the challenge fresh
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700">
            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              Ready to Challenge Your Mind? 🚀
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Choose a puzzle above and start improving your mathematical thinking today!
            </p>
            <div className="flex justify-center space-x-4">
              <Link 
                to="/"
                className="inline-flex items-center space-x-2 bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-700 transition-colors"
              >
                <span>🏠</span>
                <span>Back to Dashboard</span>
              </Link>
              <a 
                href="http://localhost:3002/mental-math-speed"
                className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >
                <span>🧮</span>
                <span>Try Mental Math</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PuzzleGamesSelection;